import { ApolloError } from '@apollo/client';
import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  FundraisingStatus,
  GetAllFundraisingsQuery,
  GetFundraisingsQuery,
  useGetAllFundraisingsLazyQuery,
  useGetFundraisingsLazyQuery,
} from '@blockpulse3/graphql/hooks';

type FundraisingsQuery = {
  fundraisings: GetAllFundraisingsQuery['fundraisings'] | GetFundraisingsQuery['fundraisings'];
  fundraisingsLoading: boolean;
  fundraisingsError: ApolloError | undefined;
};

export function useGetFundraisings(fundraisingStatus: FundraisingStatus): FundraisingsQuery {
  const { companyId = '' } = useParams();
  const location = useLocation();
  const isManaging: boolean = location.pathname?.includes('/manage');

  const [
    getAllFundraisings,
    { data: allFundraisings, loading: allFundraisingsLoading, error: allFundraisingsError },
  ] = useGetAllFundraisingsLazyQuery({ fetchPolicy: 'cache-and-network' });

  const [getFundraisings, { data, loading, error }] = useGetFundraisingsLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (isManaging) {
      getAllFundraisings({
        variables: {
          status: fundraisingStatus,
        },
      });
    } else if (companyId) {
      getFundraisings({
        variables: {
          companyId,
          status: fundraisingStatus,
        },
      });
    }
  }, [getAllFundraisings, getFundraisings, isManaging, companyId, fundraisingStatus]);

  const fundraisings = allFundraisings?.fundraisings || data?.fundraisings || [];
  const fundraisingsLoading = loading || allFundraisingsLoading;
  const fundraisingsError = error || allFundraisingsError;

  return { fundraisings, fundraisingsLoading, fundraisingsError };
}
